// Positions typed into the search box, worked out in the browser (no request): OS and Irish grid references,
// decimal degrees, and degrees, minutes and seconds (spec §3.1).
import proj4 from 'proj4';
import '@/map/projections';

export interface PositionMatch {
  kind: 'osgb' | 'irish' | 'coordinates';
  /** How it is shown in the results, tidied up: "NN 16680 71270", "56.79690, -5.00360". */
  label: string;
  /** [lon, lat]. */
  at: [number, number];
}

const letters = 'ABCDEFGHJKLMNOPQRSTUVWXYZ'; // the grids skip I

/** The digits after the letters: an even number, split in half; the point is the middle of the square they give. */
function digits(raw: string): { e: number; n: number; text: string } | undefined {
  const all = raw.replace(/\s+/g, '');
  if (all.length < 2 || all.length > 10 || all.length % 2 !== 0) return undefined;
  const half = all.length / 2;
  const size = 10 ** (5 - half);
  return {
    e: Number(all.slice(0, half)) * size + size / 2,
    n: Number(all.slice(half)) * size + size / 2,
    text: `${all.slice(0, half)} ${all.slice(half)}`,
  };
}

/** An OS grid reference: two letters and 2 to 10 digits, e.g. "NN 166 712", "nn166712", "SD 25 07". */
export function parseOsGridRef(query: string): PositionMatch | undefined {
  const m = /^([a-hj-z]{2})\s*([\d\s]+)$/i.exec(query.trim());
  if (!m) return undefined;
  const square = m[1]!.toUpperCase();
  const l1 = letters.indexOf(square[0]!);
  const l2 = letters.indexOf(square[1]!);
  const e100k = ((l1 - 2 + 25) % 5) * 5 + (l2 % 5);
  const n100k = 19 - Math.floor(l1 / 5) * 5 - Math.floor(l2 / 5);
  const d = digits(m[2]!);
  if (!d || e100k > 6 || n100k < 0 || n100k > 12) return undefined;
  const at = proj4('EPSG:27700', 'EPSG:4326', [e100k * 100000 + d.e, n100k * 100000 + d.n]) as [
    number,
    number,
  ];
  return { kind: 'osgb', label: `${square} ${d.text}`, at };
}

/** An Irish grid reference: one letter and the digits, e.g. "V 803 844", "J3374". */
export function parseIrishGridRef(query: string): PositionMatch | undefined {
  const m = /^([a-hj-z])\s*([\d\s]+)$/i.exec(query.trim());
  if (!m) return undefined;
  const letter = m[1]!.toUpperCase();
  const l = letters.indexOf(letter);
  const d = digits(m[2]!);
  if (!d) return undefined;
  const e = (l % 5) * 100000 + d.e;
  const n = (4 - Math.floor(l / 5)) * 100000 + d.n;
  const at = proj4('EPSG:29901', 'EPSG:4326', [e, n]) as [number, number];
  return { kind: 'irish', label: `${letter} ${d.text}`, at };
}

const numbers = (text: string) =>
  (text.match(/[-+]?\d+(?:[.,]\d+)?/g) ?? []).map((n) => Number(n.replace(',', '.')));

/** Degrees; degrees and minutes; or degrees, minutes and seconds. The sign is the degrees'. */
function degrees(parts: number[]): number | undefined {
  if (parts.length < 1 || parts.length > 3) return undefined;
  const [d, m = 0, s = 0] = parts as [number, number?, number?];
  if (m < 0 || m >= 60 || s < 0 || s >= 60) return undefined;
  const value = Math.abs(d) + m / 60 + s / 3600;
  return d < 0 || Object.is(d, -0) ? -value : value;
}

/**
 * Latitude and longitude: "56.7969, -5.0036", "56.7969 -5.0036", "56°47'49"N 5°0'13"W", "N56 47.8 W5 0.2".
 * Without N, S, E or W the first is the latitude, unless only the second can be.
 */
export function parseCoordinates(query: string): PositionMatch | undefined {
  const text = query.trim().toUpperCase();
  let lat: number | undefined;
  let lon: number | undefined;
  const hemi = /^([NS])?\s*([^NSEW]+?)\s*([NS])?\s*[,;]?\s*([EW])?\s*([^NSEW]+?)\s*([EW])?$/.exec(text);
  const ns = hemi && (hemi[1] ?? hemi[3]);
  const ew = hemi && (hemi[4] ?? hemi[6]);
  if (hemi && ns && ew && !(hemi[1] && hemi[3]) && !(hemi[4] && hemi[6])) {
    const a = degrees(numbers(hemi[2]!).map(Math.abs));
    const b = degrees(numbers(hemi[5]!).map(Math.abs));
    if (a === undefined || b === undefined) return undefined;
    lat = ns === 'S' ? -a : a;
    lon = ew === 'W' ? -b : b;
  } else if (/^[\d\s.,;°'"′″+-]+$/.test(text)) {
    // The two are split by a semicolon, or a comma; but "56,79 -5,00" has decimal commas, split by the space.
    const halves = text.includes(';')
      ? text.split(';')
      : /\d,\d/.test(text) && /\s/.test(text)
        ? text.split(/\s+/)
        : text.includes(',')
          ? text.split(',')
          : null;
    if (halves?.length === 2) {
      lat = degrees(numbers(halves[0]!));
      lon = degrees(numbers(halves[1]!));
    } else {
      const all = numbers(text);
      if (all.length === 2 || all.length === 4 || all.length === 6) {
        lat = degrees(all.slice(0, all.length / 2));
        lon = degrees(all.slice(all.length / 2));
      }
    }
    if (lat !== undefined && lon !== undefined && Math.abs(lat) > 90 && Math.abs(lon) <= 90)
      [lat, lon] = [lon, lat];
  }
  if (lat === undefined || lon === undefined || Math.abs(lat) > 90 || Math.abs(lon) > 180) return undefined;
  return { kind: 'coordinates', label: `${lat.toFixed(5)}, ${lon.toFixed(5)}`, at: [lon, lat] };
}

/** Everything the query could be as a position, most likely first. */
export function parsePosition(query: string): PositionMatch[] {
  return [parseOsGridRef(query), parseIrishGridRef(query), parseCoordinates(query)].filter(
    (m): m is PositionMatch => m !== undefined,
  );
}
